<template>
  <div class="container-fluid mt-5">
    <div>
      <card
        class="no-border-card"
        body-classes="px-0 pb-1"
        footer-classes="pb-2"
      >
        <template slot="header">
          <div class="row">
            <div class="col-8">
              <h3 class="mb-0 view-header-buttons">
                <span class="text-before-button">{{ subscription.code }}</span>
                <base-button
                  v-if="$currentUserCan($permissions.PERM_EDIT_SUBSCRIPTIONS)"
                  type="primary"
                  icon
                  size="sm"
                  @click="editSubscription()"
                  :disabled="loading"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_EDIT_SUBSCRIPTIONS) &&
                    $currentUserCan($permissions.PERM_CREATE_INVOICES)
                  "
                  type="info"
                  icon
                  size="sm"
                  @click="renewSubscription()"
                  :disabled="loading"
                >
                  <span class="btn-inner--icon">
                    {{ $t("SUBSCRIPTIONS.RENEW") }}
                    <i class="fas fa-plus-circle"></i>
                  </span>
                </base-button>

                <base-button
                  v-if="
                    $currentUserCan($permissions.PERM_DELETE_SUBSCRIPTIONS) &&
                    false
                  "
                  type="danger"
                  icon
                  size="sm"
                  @click="deleteSubscription()"
                  :disabled="loading"
                >
                  <span class="btn-inner--icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </base-button>
              </h3>
            </div>
            <div class="col-4 text-right">
              <base-button
                @click="goBack()"
                type="button"
                class="btn btn-sm btn-primary"
              >
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>

              <notification-subscription
                :objectType="'subscriptions'"
                :objectId="subscription.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
            </div>
          </div>
        </template>
        <div>
          <div
            class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
          >
            <tabs
              fill
              class="flex-column flex-md-row"
              tabNavWrapperClasses="nav-wrapper"
              tabNavClasses="nav nav-pills nav-fill"
              value="global"
            >
              <card shadow>
                <tab-pane title="global" id="1" :active="true">
                  <span slot="title">
                    <i class="ni ni-cloud-upload-96" />
                    {{ $t("COMMON.GLOBAL") }}
                  </span>
                  <subscription-view-global :subscription="subscription" />
                </tab-pane>

                <tab-pane
                  title="logs"
                  id="999"
                  v-if="$currentUserCan($permissions.PERM_VIEW_ANY_LOGS)"
                >
                  <span slot="title">
                    <i class="fa fa-file" />
                    {{ $t("COMMON.LOGS") }}
                  </span>
                  <subscription-view-logs :subscription="subscription" />
                </tab-pane>
              </card>
            </tabs>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import swal from "sweetalert2";
import { cloneDeep } from "lodash";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { Tabs, TabPane } from "@/components";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import defaultSubscription from "./defaultSubscription";
import SubscriptionViewGlobal from "./partials/SubscriptionViewGlobal.vue";
import SubscriptionViewLogs from "./partials/SubscriptionViewLogs.vue";

export default {
  layout: "DashboardLayout",

  components: {
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    Tabs,
    TabPane,
    SubscriptionViewGlobal,
    SubscriptionViewLogs,
    NotificationSubscription,
  },

  mixins: [requestErrorMixin],

  data() {
    return {
      subscription: cloneDeep(defaultSubscription),
      loading: false,
    };
  },

  computed: {},

  watch: {},

  created() {
    this.get();
  },

  methods: {
    async get() {
      try {
        const id = this.$route.params.id;
        await this.$store.dispatch("subscriptions/get", id);
        this.subscription = this.$store.getters["subscriptions/subscription"];
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async editSubscription() {
      this.$router.push({
        name: "Edit Subscription",
        params: { id: this.subscription.id },
      });
    },

    async deleteSubscription() {
      const confirmation = await swal.fire({
        title: this.$t("SUBSCRIPTIONS.DELETE_THIS_SUBSCRIPTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "subscriptions/destroy",
            this.subscription.id
          );
          await this.goBack();
          this.$notify({
            type: "success",
            message: this.$t("SUBSCRIPTIONS.SUBSCRIPTION_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async renewSubscription() {
      this.loading = true;
      try {
        const invoice = await this.$store.dispatch(
          "subscriptions/renew",
          this.subscription.id
        );

        this.$notify({
          type: "success",
          message: this.$t("SUBSCRIPTIONS.SUBSCRIPTION_INVOICE_ADDED"),
        });
        this.loading = false;

        this.$router.push({
          name: "View ResellerInvoice",
          params: { id: invoice.id },
        });
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
        this.setApiValidation(error.response.data.errors);
      }
    },

    goBack() {
      this.$router.push({ name: "List Subscriptions" });
    },
  },
};
</script>
